import {
  Droplets,
  Monitor,
  Keyboard,
  Layers,
  BatteryCharging,
  MousePointer2,
  ScanSearch,
  Cpu,
  Smartphone,
} from "lucide-react";

const macServices = [
  {
    id: "fuktskade",
    name: "Fuktskade-reparasjon",
    description:
      "Rens, tørking og komponentbytte etter væskeskade. Vi redder det som kan reddes — og forebygger korrosjon før den sprer seg.",
    Icon: Droplets,
    testId: "service-card-fuktskade",
  },
  {
    id: "skjerm",
    name: "Skjermbytte",
    description:
      "Bytte av sprukne, flekkete eller defekte skjermer på MacBook og iMac. Full farge- og berøringsfunksjonalitet bevart.",
    Icon: Monitor,
    testId: "service-card-skjerm",
  },
  {
    id: "tastatur",
    name: "Tastaturbytte",
    description:
      "Erstatning av defekte, stikkende eller fastlåste taster. Vi fikser også problemer med bakgrunnsbelysning.",
    Icon: Keyboard,
    testId: "service-card-tastatur",
  },
  {
    id: "topcase",
    name: "Topp-case bytte",
    description:
      "Komplett bytte av topp-case når tastatur, batteri og håndstøtte må fornyes som én enhet.",
    Icon: Layers,
    testId: "service-card-topcase",
  },
  {
    id: "batteri",
    name: "Batteribytte",
    description:
      "Nytt batteri med opprinnelig kapasitet og lang levetid. Vi kalibrerer og tester før retur.",
    Icon: BatteryCharging,
    testId: "service-card-batteri",
  },
  {
    id: "trackpad",
    name: "Trackpad-reparasjon og bytte",
    description:
      "Vi reparerer eller bytter trackpad som ikke klikker, registrerer feil eller har sluttet å virke.",
    Icon: MousePointer2,
    testId: "service-card-trackpad",
  },
  {
    id: "diagnostikk",
    name: "Diagnostikk og feilsøking",
    description:
      "Grundig undersøkelse av maskinvare og programvare for å finne årsaken — også når feilen er uklar eller sporadisk.",
    Icon: ScanSearch,
    testId: "service-card-diagnostikk",
  },
  {
    id: "oppgradering",
    name: "Oppgradering og vedlikehold",
    description:
      "Rensing av vifter, oppdatering av programvare og oppgradering av lagring eller minne for bedre ytelse.",
    Icon: Cpu,
    testId: "service-card-oppgradering",
  },
];

const mobileServices = [
  {
    id: "mobile-skjerm",
    name: "Skjermbytte",
    description:
      "Bytte av sprukken eller defekt skjerm på iPhone, iPad og Apple Watch.",
    Icon: Monitor,
    testId: "service-card-mobile-skjerm",
  },
  {
    id: "mobile-batteri",
    name: "Batteribytte",
    description:
      "Nytt batteri som gir tilbake opprinnelig kapasitet og brukstid på iPhone, iPad og Apple Watch.",
    Icon: BatteryCharging,
    testId: "service-card-mobile-batteri",
  },
];

function ServiceCard({ service }) {
  const { name, description, Icon, testId } = service;
  return (
    <article
      className="group bg-white rounded-3xl p-7 border border-black/[0.05] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_60px_-20px_rgba(0,0,0,0.12)]"
      data-testid={testId}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5F5F7] text-[#1D1D1F]">
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <h3 className="font-display mt-7 text-xl font-semibold tracking-tight text-[#1D1D1F]">
        {name}
      </h3>
      <p className="mt-2 text-sm text-[#86868B] leading-relaxed">
        {description}
      </p>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="tjenester"
      className="py-24 md:py-32 bg-[#F5F5F7]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-[#86868B]">
            Tjenester
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
            Spesialister på Apple.
          </h2>
          <p className="mt-5 text-lg text-[#1D1D1F]/70 leading-relaxed">
            Vi gjør et bredt utvalg reparasjoner på Mac, og tilbyr skjerm- og
            batteribytte på iPhone, iPad og Apple Watch — alt utføres trygt via
            post.
          </p>
        </div>

        {/* Mac & iMac — full service catalogue */}
        <div className="mt-14">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2.5">
                <Cpu size={16} strokeWidth={1.75} className="text-[#1D1D1F]" />
                <span className="text-xs uppercase tracking-[0.18em] text-[#1D1D1F]/80">
                  MacBook og iMac
                </span>
              </div>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D1D1F]">
                Full reparasjonsmeny.
              </h3>
            </div>
            <p className="text-sm text-[#86868B] max-w-md">
              Åtte spesialiserte tjenester — fra raske inngrep til komplekse
              reparasjoner.
            </p>
          </div>

          <div
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="services-grid-mac"
          >
            {macServices.map((s) => (
              <ServiceCard key={s.id} service={s} />
            ))}
          </div>
        </div>

        {/* iPhone, iPad, Apple Watch — limited offering */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2.5">
                <Smartphone size={16} strokeWidth={1.75} className="text-[#1D1D1F]" />
                <span className="text-xs uppercase tracking-[0.18em] text-[#1D1D1F]/80">
                  iPhone, iPad og Apple Watch
                </span>
              </div>
              <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D1D1F]">
                Skjerm- og batteribytte.
              </h3>
            </div>
            <p className="text-sm text-[#86868B] max-w-md">
              På disse enhetene tilbyr vi kun skjerm- og batteribytte — de mest
              vanlige feilene, raskt og pålitelig.
            </p>
          </div>

          <div
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="services-grid-mobile"
          >
            {mobileServices.map((s) => (
              <ServiceCard key={s.id} service={s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
